import type { ReactNode } from "react";
import {
  BUSINESS_NAME,
  CONTACT_EMAIL,
  CONTACT_PHONE,
  HOSTING_PROVIDER,
  PRIVACY_POLICY_EFFECTIVE_DATE,
  REPRESENTATIVE_NAME,
  SOURCE_RETENTION_DAYS,
  amendmentInForce,
  paymentOrderName,
  pendingAmendment,
} from "@makit/shared";

import { AmendmentBlock } from "../../features/policy-amendment/AmendmentNotice";
import { PublicLayout } from "./PublicLayout";

/** `P-04` 개인정보처리방침. 개인정보를 받기 시작하는 순간(운영 로그인)부터 시행 중이어야 하는 문서다.
 *
 * **글자의 정본은 PRD `7.0` 「로그인 전 화면의 확정 문구」 (4)이다**(2026-10-04 `design-manager` 확정.
 * 법무 `4.16.2` 게시본에서 실측에 맞춰 고친 판). 이 파일은 그 문장을 옮기기만 한다.
 *
 * ⛔ **실측과 다른 문장을 넣지 않는다**(법무 `4.16.0`-1). 이 판이 선 실측(2026-10-04, `coding-agent`):
 * - 제5조 · 제6조의 **Modal 줄**: 운영 `apps/api/wrangler.toml` `[vars]` 의 `CUTOUT_PROVIDER = "imaging"` 과
 *   `services/imaging/README.md` 의 운영 Worker 적용 기록. 운영 Worker 의 시크릿 목록은 직접 조회하지 못했다.
 *   공급자를 바꾸는 배포는 이 두 조항을 함께 고친다.
 * - 제3조 **「이름과 이메일 · 작업 내용은 탈퇴 뒤에도 남아 있습니다」**: `apps/api/src/lib/auth.ts`
 *   `withdrawSeller` 가 그 둘을 지우지 않는다(법무 `L-C33`(a)(b)). 코드가 지우게 되면 이 줄을 바꾼다.
 * - 제2조: `INSERT INTO "Consent"` 0건. 학습 이용 동의를 받는 경로가 생기면 이 조항을 다시 쓰고, 그 커밋은
 *   변호사 검토 발동 조건(T3)이다.
 * - 제1조 결제 줄: 결제 수단은 토스 결제위젯 설정이 정하고 저장소에서 잴 수 없다. 그래서 「카드인 경우」로 적는다.
 * - 제8조 3: 웹에 탈퇴 화면이 없다. 생기면 이 줄과 약관 제11조 1 을 함께 고친다.
 * - 사진의 **파일 이름**(`JobImage.originalFilename` 과 그 사본 `JobEvent.detail`)은 30일 원본 배치
 *   (`markImageSourcePurged`)도 탈퇴 파기(`markJobAssetsPurged`)도 지우지 않는다. 그래서 제1조에서 작업 내용
 *   쪽에 두고 제3조에 「남습니다」로 적었다. 파기 때 비우는 코드가 서면 이 두 자리를 바꾼다.
 * - 제3조 **「만들어 드린 결과」 행**(2026-10-10 사용자 결정. 토스 카드사 심사 보완 요청): 옛 「계정이 있는
 *   동안」을 「결제하신 날부터 1개월」로 바꿨다. 기간 뒤에 「다시 만들 수 있는 마지막 날이 더 늦으면 그날까지」를
 *   붙인 것은 다음 서버 PR 의 실제 파기 기한이 max(결제한 날 + 달력 1개월, 올린 날 + 30일)이라서다. 2월에는
 *   1개월이 30일보다 짧아 그 꼬리가 없으면 적힌 기간보다 오래 들고 있게 된다. ⚠️ 1개월이 지난 결과를 지우는
 *   장치는 아직 없다(다음 서버 PR). 첫 운영 결제가 1개월에 닿기 전에 서야 이 행이 참이다.
 * - 제1조 카카오 이름: `lib/oauth.ts` 는 `kakao_account.name` 만 읽는다. 별명은 그 전에 가입한 셀러의 옛
 *   행에만 남아 있다.
 * - 제1조 · 제3조 **기본 배경 행은 바뀐 뒤 판에만 있다**(사용자 결정 2026-10-28 (다)안 · 법무 `L-C38` · `4.19`(8)).
 *   지금 판(시행일 전 `/privacy`)에는 없다. 시행일 전에 게시본이 받지 않는 것을 받는다고 말하지 않기 위해서다
 *   (`L-C38`-8). 실측: 서버가 `SellerSetting` 에 쓰는 칸은 `defaultBackgroundColor` 하나다(`routes/auth.ts`
 *   `SELLER_SETTING_WRITABLE_COLUMNS`). 고르는 조작이 곧바로 서버로 가고, 시행일에 폰의 값을 옮기지 않는다. 탈퇴
 *   배치(`lib/auth.ts` `withdrawSeller`)가 그 행을 지운다. 운영은 시행일까지 쓰기가 꺼져 있다(`ACCOUNT_SETTINGS_WRITE`).
 *   ⛔ 사진 이용 스위치는 이 개정에 들지 않는다(서버에 쓰지 않는다). 글자는 법무 `4.19`(8) 최종 문안이다.
 *
 * ## 지금 판과 바뀐 뒤 판 (PRD `7.5.17` `P-04b`)
 *
 * 개정 값(`PENDING_POLICY_AMENDMENT`)의 시행일 **전**에는 `/privacy` 가 지금 판(시행일 `PRIVACY_POLICY_EFFECTIVE_DATE`)과
 * `{시행일}부터 바뀝니다.` 블록을, `/privacy/next`(`version="next"`)가 바뀐 뒤 판을 그린다. 시행일 **뒤**에는 둘 다
 * 바뀐 뒤 판이고 블록이 사라진다(배포 없이 날짜로 넘어간다). 그 뒤 배포에서 바뀐 뒤 판을 본문에 접어 넣고
 * `PRIVACY_POLICY_EFFECTIVE_DATE` 를 그 시행일로 옮기고 개정 값을 `null` 로 되돌린다. 시행일이 자리 표시면 개정이
 * 없는 것으로 본다(`/privacy/next` 도 지금 판). 자리 표시를 게시하지 않는다.
 *
 * 상호 · 대표자 · 연락처 · 서버 운영 회사 · 보관 일수 · 주문 이름은 상수에서 읽는다. 쿠키 기간(30일 · 10분)은
 * `apps/api/src/lib/auth.ts` 의 `SESSION_TTL_DAYS` · `LOGIN_ATTEMPT_TTL_MINUTES` 를 옮긴 것이다. 그 값이 바뀌면
 * 제7조도 바꾼다. */
export function PrivacyScreen({ version = "current" }: { version?: "current" | "next" }) {
  const pending = pendingAmendment();
  const inForce = amendmentInForce();
  /** 바뀐 뒤 판을 그리는가. 이미 시행됐거나, 시행일 전인데 바뀐 뒤 전문을 연 경우. */
  const amended = inForce ?? (version === "next" ? pending : null);
  // 개정이 이 문서(개인정보처리방침)의 것일 때만 이 화면에 적용한다. 장치는 문서마다 같은 값을 쓴다.
  const ours = amended && amended.docName === "개인정보처리방침" ? amended : null;
  const block = version === "current" && pending && pending.docName === "개인정보처리방침" ? pending : null;
  return (
    <PublicLayout title="개인정보처리방침 · makit">
      <article className="policy">
        <h1>개인정보처리방침</h1>
        <p className="meta">
          시행일{" "}
          {ours ? (
            <span className="mono">{ours.effectiveDate}</span>
          ) : (
            <span className="mono">{PRIVACY_POLICY_EFFECTIVE_DATE}</span>
          )}
        </p>
        {block ? <AmendmentBlock amendment={block} /> : null}

        <p className="prose">
          {BUSINESS_NAME}(이하 「우리」)는 makit 을 쓰시는 셀러의 개인정보를 아래와 같이 처리합니다. 이 방침은 makit
          앱과 웹에 함께 적용됩니다.
        </p>

        <h2>제1조 받는 정보와 쓰는 곳</h2>
        <PolicyTable
          head={["받는 정보", "받는 때와 방법", "쓰는 곳"]}
          rows={[
            [
              "네이버 계정을 가리키는 값, 이름, 이메일",
              "네이버로 가입 · 로그인하실 때 네이버에서 받습니다. 이름과 이메일 중 하나라도 받지 못하면 가입되지 않습니다",
              "계정 만들기와 로그인, 내 계정 화면 표시, 결제와 돌아가는 금액에 관한 연락",
            ],
            [
              "카카오 계정을 가리키는 값, 이름(카카오에 등록하신 이름. 예전에 가입하신 분은 그때 받은 카카오 별명이 이름으로 남아 있을 수 있습니다), 이메일",
              "카카오로 가입 · 로그인하실 때 카카오에서 받습니다. 이름과 이메일은 카카오에서 제공에 동의하신 경우에만 받으며, 없어도 가입됩니다",
              "위와 같습니다",
            ],
            [
              "가입 · 로그인 · 탈퇴 시각, 로그인 상태를 이어 주는 값",
              "서비스를 쓰시는 동안 생깁니다",
              "로그인 유지, 앱에서 로그아웃하면 웹도 함께 끊기게 하는 일, 부정 이용 방지",
            ],
            [
              "올리신 사진, 사진 파일에 들어 있는 촬영 정보(촬영 위치 · 기기 · 시각이 담겨 있으면 그것까지)",
              "사진을 올리실 때",
              "배경 지우기와 상세페이지 이미지 만들기, 다시 만들기",
            ],
            ["만들어 드린 결과(배경 지운 사진, 상세페이지 이미지, 미리보기)", "만들 때", "보여 드리기와 받으실 수 있게 하기"],
            [
              "작업 내용(상품명, 배경 색, 스펙, 사진 순서, 올리신 사진의 파일 이름 등)과 작업 기록(언제 무엇을 고쳤는지, 고르신 상품 종류, 다시 만들지 않기로 하신 선택)",
              "작업하실 때",
              "작업 이어서 하기, 이전 판으로 되돌리기, 문의에 답하기",
            ],
            ...(ours
              ? [
                  [
                    "기본 배경으로 고르신 색",
                    "앱이나 웹의 환경설정에서 기본 배경을 고르실 때",
                    "앱과 웹에서 같은 색을 쓰게 하기, 새로 만드는 상세페이지의 첫 배경으로 쓰기",
                  ],
                ]
              : []),
            [
              "결제 기록(주문 번호, 금액, 장수, 단가, 결제 수단 이름(카드인 경우 카드 끝 네 자리), 결제사 거래 번호, 승인 번호, 결제 시각)과 돌아간 금액 기록",
              "결제하실 때와 금액이 돌아갈 때",
              "결제 내역 표시, 결제 취소, 법령에 따른 기록 보존",
            ],
            [
              "접속 기록(접속 주소(IP), 요청 시각, 요청한 주소, 브라우저 · 앱 정보)",
              "서비스를 쓰시는 동안 생깁니다",
              "오류 확인, 부정 이용 방지, 짧은 시간 안의 반복 로그인 시도 제한",
            ],
          ]}
        />
        <p className="prose">
          우리는 프로필 사진, 성별, 생일, 연령대, 휴대전화번호를 받지 않습니다. 네이버 · 카카오가 발급하는 접근
          열쇠(토큰)를 보관하지 않습니다. 카드 번호 · 유효기간 · CVC 는 결제창에서 결제사로 바로 가며 우리가 받지
          않습니다. 올리신 사진의 촬영 정보는 지우지 않고 받은 그대로 보관합니다. 촬영 위치가 남기를 원하지 않으시면
          올리시기 전에 지워 주세요.
        </p>

        <h2>제2조 올리신 사진을 서비스 개선에 쓰는지</h2>
        <p className="prose">
          지금은 올리신 사진을 배경 지우기 품질 개선이나 다른 목적에 쓰지 않습니다. 사진은 제1조의 쓰는 곳에만
          씁니다. 품질 개선에 쓰기 시작하려면 먼저 이 방침과 이용약관을 바꿔 알려 드리고, 직접 켜신 분의 사진만,
          켜신 뒤에 올리신 것만 씁니다.
        </p>

        <h2>제3조 보관 기간과 지우는 때</h2>
        <PolicyTable
          head={["무엇", "보관 기간", "지우는 때와 방법"]}
          rows={[
            [
              "올리신 사진",
              `올리신 날부터 ${SOURCE_RETENTION_DAYS}일. 결제하셨는지와 관계없습니다`,
              `매일 한 번 ${SOURCE_RETENTION_DAYS}일이 지난 사진을 지웁니다. 그 뒤에는 그 사진으로 다시 만들 수 없습니다. 사진의 파일 이름은 이때 지우지 않고 작업 내용과 함께 남습니다`,
            ],
            [
              "만들어 드린 결과",
              `결제하신 날부터 1개월. 다시 만들 수 있는 마지막 날(올리신 날부터 ${SOURCE_RETENTION_DAYS}일)이 더 늦으면 그날까지`,
              "그 기간이 지나면 결과를 지웁니다. 그 뒤에는 받으실 수 없습니다. 그 전에 탈퇴하시면 탈퇴하실 때 올리신 사진과 함께 지웁니다",
            ],
            ["계정을 가리키는 값과 로그인 상태, 로그인 진행 기록", "탈퇴하실 때까지", "탈퇴하시면 지웁니다"],
            [
              "이름과 이메일",
              "지금은 탈퇴하신 뒤에도 남아 있습니다",
              "지우는 때를 정하면 이 방침을 바꿔 알려 드립니다",
            ],
            [
              "작업 내용과 작업 기록",
              "지금은 탈퇴하신 뒤에도 남아 있습니다(탈퇴한 계정의 것으로 표시해 둡니다)",
              "위와 같습니다",
            ],
            ...(ours ? [["기본 배경으로 고르신 색", "탈퇴하실 때까지", "탈퇴하시면 지웁니다"]] : []),
            [
              "결제 기록과 돌아간 금액 기록",
              "법령이 정한 기간(전자상거래 거래 기록 5년) 이상 보관합니다",
              "그 기간이 지난 뒤 지우는 때를 정하면 이 방침을 바꿔 알려 드립니다",
            ],
            ["접속 기록", "서버 운영을 맡긴 Cloudflare 가 정한 기간", "그 기간이 지나면 Cloudflare 가 지웁니다"],
          ]}
        />
        <p className="prose">
          지울 때는 저장소에서 파일을 삭제하고, 데이터베이스에서는 값을 지우거나 지운 시각을 남깁니다.
        </p>

        <h2>제4조 다른 곳에 주는 경우(제3자 제공)</h2>
        <p className="prose">
          우리는 셀러의 개인정보를 다른 회사에 팔거나 넘기지 않습니다. 법령에 따라 요구받은 경우는 예외입니다.
        </p>

        <h2>제5조 처리를 맡기는 곳(처리 위탁)</h2>
        <PolicyTable
          head={["맡기는 곳", "맡기는 일", "가는 정보"]}
          rows={[
            [HOSTING_PROVIDER, "서버 운영, 사진 · 결과 파일 저장, 데이터베이스, 서버 기록", "제1조의 정보 전부"],
            ["Modal Labs, Inc.", "배경 지우기와 상세페이지 이미지 만들기", "올리신 사진, 배경 지운 사진, 고르신 배경 색"],
            [
              "토스페이먼츠 주식회사",
              "결제와 결제 취소",
              `주문 번호, 금액, 주문 이름(예: 「${paymentOrderName(8)}」), 결제창에 직접 넣으신 결제 정보`,
            ],
          ]}
        />

        <h2>제6조 국외로 가는 정보(국외 이전)</h2>
        <PolicyTable
          head={["받는 곳", "나라", "가는 정보", "때와 방법", "보관 기간"]}
          rows={[
            [
              HOSTING_PROVIDER,
              "미국 회사이며 여러 나라의 서버를 씁니다. 사진 저장소는 아시아 · 태평양 지역으로 지정했고, 데이터베이스가 있는 나라는 지정하지 않았습니다",
              "제1조의 정보 전부",
              "서비스를 쓰실 때마다 네트워크로",
              "제3조의 기간",
            ],
            [
              "Modal Labs, Inc.",
              "미국",
              "올리신 사진, 배경 지운 사진, 배경 색",
              "배경 지우기를 시작하실 때 네트워크로",
              "처리를 마친 뒤 Modal 이 정한 기간",
            ],
          ]}
        />
        <p className="prose">
          국외로 옮기는 것을 원하지 않으시면 서비스를 쓰실 수 없습니다. 이 경우 탈퇴하실 수 있습니다(제8조).
        </p>

        <h2>제7조 브라우저에 저장하는 값(쿠키)</h2>
        <p className="prose">
          웹은 로그인 상태를 이어 주는 쿠키 하나(30일)와 로그인하는 동안에만 쓰는 쿠키 하나(10분)를 씁니다. 광고 ·
          분석용 쿠키는 쓰지 않습니다. 최근 작업 목록을 브라우저에 저장하지만 서버로 보내지 않습니다. 브라우저
          설정에서 쿠키를 막으실 수 있으며, 그러면 웹에 로그인하실 수 없습니다.
        </p>

        <h2>제8조 셀러가 하실 수 있는 일</h2>
        <ol>
          <li className="prose">
            우리가 가진 내 정보를 보여 달라고, 고쳐 달라고, 지워 달라고, 처리를 멈춰 달라고 요구하실 수
            있습니다(열람 · 정정 · 삭제 · 처리정지 요구).
          </li>
          <li className="prose">
            요구는 제10조의 창구로 하시면 됩니다. 대리인을 통해서도 하실 수 있습니다. 받은 날부터 10일 안에
            답합니다.
          </li>
          <li className="prose">
            탈퇴는 앱의 마이페이지에서 하실 수 있습니다. 웹만 쓰시면 제10조의 창구로 탈퇴를 요청하실 수 있습니다.
          </li>
          <li className="prose">
            법령이 보관을 요구하는 기록(제3조의 결제 기록 등)은 지워 달라고 하셔도 그 기간 동안 보관합니다.
          </li>
        </ol>

        <h2>제9조 안전하게 지키는 방법</h2>
        <p className="prose">
          로그인 상태를 이어 주는 값은 원래 값이 아니라 되돌릴 수 없게 바꾼 값으로만 보관합니다. 결제 비밀 키 같은
          비밀 값은 소스 코드가 아니라 별도의 비밀 저장소에 둡니다. 셀러는 자기 작업과 사진만 볼 수 있습니다.
          우리가 남기는 오류 기록에는 이름 · 이메일 · 사진 주소를 넣지 않습니다.
        </p>

        <h2>제10조 개인정보 보호책임자와 창구</h2>
        <ul>
          <li className="prose">
            개인정보 보호책임자: {REPRESENTATIVE_NAME}(대표) · <span className="mono">{CONTACT_EMAIL}</span>
          </li>
          <li className="prose">
            창구: <span className="mono">{CONTACT_EMAIL}</span> · <span className="mono">{CONTACT_PHONE}</span>
          </li>
        </ul>

        <h2>제11조 방침이 바뀔 때</h2>
        <p className="prose">
          이 방침을 바꾸면 시행 7일 전에 앱과 웹에 알립니다. 셀러에게 불리하게 바뀌는 경우에는 시행 30일 전에
          알립니다.
        </p>
      </article>
    </PublicLayout>
  );
}

/** 처리방침의 표 하나. 좁은 화면에서는 표만 가로로 민다(글을 자르지 않는다). */
function PolicyTable({ head, rows }: { head: readonly string[]; rows: readonly (readonly ReactNode[])[] }) {
  return (
    <div className="policytable">
      <table>
        <thead>
          <tr>
            {head.map((h) => (
              <th key={h} scope="col">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => (
                <td key={j}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
